/**
 * TypeScript mirrors of the realtime WebSocket protocol exposed by
 * notification-management. See `REALTIME-WEBSOCKET.md` in the backend repo
 * for the authoritative specification. These types are purely structural —
 * no runtime behaviour lives in this file.
 */

import type {
  WalletTimeLimitSettingValue,
  WalletWithdrawalLimitSettingValue,
} from '@shared/models/wallet.model';

export enum WsEventType {
  IDENTIFIED = 'IDENTIFIED',
  IDENTIFY_FAILED = 'IDENTIFY_FAILED',
  DEVICE_LIST = 'DEVICE_LIST',
  DEVICE_CONNECTED = 'DEVICE_CONNECTED',
  DEVICE_DISCONNECTED = 'DEVICE_DISCONNECTED',

  USER_ONBOARDED = 'USER_ONBOARDED',
  USER_ROLE_CHANGED = 'USER_ROLE_CHANGED',
  USER_DELETED = 'USER_DELETED',
  USER_ACCOUNT_FROZEN = 'USER_ACCOUNT_FROZEN',
  USER_ACCOUNT_UNFROZEN = 'USER_ACCOUNT_UNFROZEN',
  USER_ACCOUNT_BLOCKED = 'USER_ACCOUNT_BLOCKED',
  USER_ACCOUNT_UNBLOCKED = 'USER_ACCOUNT_UNBLOCKED',

  ACCOUNT_CREATED = 'ACCOUNT_CREATED',
  ACCOUNT_FROZEN = 'ACCOUNT_FROZEN',
  ACCOUNT_UNFROZEN = 'ACCOUNT_UNFROZEN',
  ACCOUNT_BLOCKED = 'ACCOUNT_BLOCKED',
  ACCOUNT_UNBLOCKED = 'ACCOUNT_UNBLOCKED',

  AUTH_LOGIN_SUCCESS = 'AUTH_LOGIN_SUCCESS',
  /** Silent, OWNER sockets only: someone in the account signed in (login history has a new row). */
  LOGIN_RECORDED = 'LOGIN_RECORDED',
  SESSION_CREATED = 'SESSION_CREATED',
  SESSION_REVOKED = 'SESSION_REVOKED',
  USER_LOGGED_OUT = 'USER_LOGGED_OUT',
  USER_LOGGED_OUT_ALL = 'USER_LOGGED_OUT_ALL',
  USER_PASSWORD_CHANGED = 'USER_PASSWORD_CHANGED',
  USER_PASSWORD_RESET = 'USER_PASSWORD_RESET',
  PASSWORD_FORGET_TRIGGERED = 'PASSWORD_FORGET_TRIGGERED',
  TOKEN_REUSE_DETECTED = 'TOKEN_REUSE_DETECTED',
  GOOGLE_AUTH_SETUP_COMPLETED = 'GOOGLE_AUTH_SETUP_COMPLETED',
  GOOGLE_AUTH_DISABLED = 'GOOGLE_AUTH_DISABLED',
  EMAIL_CHANGED = 'EMAIL_CHANGED',

  VAULT_ENABLED = 'VAULT_ENABLED',
  VAULT_DISABLED = 'VAULT_DISABLED',
  VAULT_TOTAL_BALANCE_UPDATED = 'VAULT_TOTAL_BALANCE_UPDATED',
  ACCOUNT_TOTAL_BALANCE_UPDATED = 'ACCOUNT_TOTAL_BALANCE_UPDATED',
  WALLET_CREATED = 'WALLET_CREATED',
  WALLET_UPDATED = 'WALLET_UPDATED',
  WALLET_STATUS_CHANGED = 'WALLET_STATUS_CHANGED',
  WALLET_BALANCE_UPDATED = 'WALLET_BALANCE_UPDATED',
  WHITELIST_ADDRESS_CREATED = 'WHITELIST_ADDRESS_CREATED',
  WHITELIST_ADDRESS_ENABLED = 'WHITELIST_ADDRESS_ENABLED',
  WHITELIST_ADDRESS_DISABLED = 'WHITELIST_ADDRESS_DISABLED',
  WHITELIST_ADDRESS_DELETED = 'WHITELIST_ADDRESS_DELETED',
  WITHDRAWAL_OPERATION_ADDED = 'WITHDRAWAL_OPERATION_ADDED',
  WITHDRAWAL_OPERATION_UPDATED = 'WITHDRAWAL_OPERATION_UPDATED',

  TRANSACTION_FINALIZED = 'TRANSACTION_FINALIZED',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED',
  /**
   * Granular signing-step progress (step 3). Replaces the legacy
   * per-2s polling of `signing-notification-channels`. Carries a
   * `milestone` token — one of `SUBMITTED`, `POLLING_STARTED`,
   * `COMPLETED`, `FAILED` per WEBSOCKET-EVENTS.md. The envelope's
   * pre-localized `title` / `message` strings cover display; the
   * milestone token is only used by the data-slice routing in the
   * dispatcher.
   */
  SIGNING_PROGRESS_UPDATED = 'SIGNING_PROGRESS_UPDATED',
  /**
   * A signed tx was broadcast and a chain `txid` is now available (step 4 —
   * the "Broadcast succeeded / TX Hash" moment). Carries the txid, the
   * pre-built explorer URL, and the resulting projection status so the detail
   * page renders the hash without re-fetching `/network-details`.
   */
  TRANSACTION_BROADCASTED = 'TRANSACTION_BROADCASTED',
  /**
   * On-chain confirmation count flipped for a broadcast tx (step 4).
   * Drives the blockchain-details slice without re-polling
   * `/blockchain-details`. Fired frequently — intentionally not on the
   * bell whitelist; only the data slice consumes it.
   */
  BLOCKCHAIN_CONFIRMATIONS_UPDATED = 'BLOCKCHAIN_CONFIRMATIONS_UPDATED',

  POLICY_CREATED = 'POLICY_CREATED',
  POLICY_UPDATED = 'POLICY_UPDATED',
  POLICY_DELETED = 'POLICY_DELETED',
  POLICY_ACTIVATED = 'POLICY_ACTIVATED',
  POLICY_DEACTIVATED = 'POLICY_DEACTIVATED',
  POLICY_CREATE_REQUESTED = 'POLICY_CREATE_REQUESTED',
  POLICY_UPDATE_REQUESTED = 'POLICY_UPDATE_REQUESTED',
  POLICY_ACTIVATE_REQUESTED = 'POLICY_ACTIVATE_REQUESTED',
  POLICY_DEACTIVATE_REQUESTED = 'POLICY_DEACTIVATE_REQUESTED',
  POLICY_DELETE_REQUESTED = 'POLICY_DELETE_REQUESTED',

  APPROVAL_REQUESTED = 'APPROVAL_REQUESTED',
  APPROVAL_APPROVED = 'APPROVAL_APPROVED',
  APPROVAL_REJECTED = 'APPROVAL_REJECTED',
  APPROVAL_CANCELED = 'APPROVAL_CANCELED',
  APPROVAL_STAGE_COMPLETED = 'APPROVAL_STAGE_COMPLETED',
  APPROVAL_REQUEST_EXPIRED = 'APPROVAL_REQUEST_EXPIRED',
  KILL_SWITCH_EXECUTED = 'KILL_SWITCH_EXECUTED',
  KILL_SWITCH_RELEASED = 'KILL_SWITCH_RELEASED',
}

export enum DeviceType {
  CHROME = 'CHROME',
  FIREFOX = 'FIREFOX',
  OPERA = 'OPERA',
  SAFARI = 'SAFARI',
  EDGE = 'EDGE',
  MOBILE_IOS = 'MOBILE_IOS',
  MOBILE_ANDROID = 'MOBILE_ANDROID',
  OTHER = 'OTHER',
}

export enum SupportedLanguage {
  EN = 'en',
  FA = 'fa',
}

export enum IdentifyFailureReason {
  TOKEN_MISSING = 'TOKEN_MISSING',
  TOKEN_INVALID = 'TOKEN_INVALID',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  SESSION_REVOKED = 'SESSION_REVOKED',
  USER_LOGGED_OUT = 'USER_LOGGED_OUT',
  IDENTIFY_TIMEOUT = 'IDENTIFY_TIMEOUT',
  ALREADY_IDENTIFIED = 'ALREADY_IDENTIFIED',
}

export enum DisconnectReason {
  CLIENT_CLOSED = 'CLIENT_CLOSED',
  NETWORK_DROPPED = 'NETWORK_DROPPED',
  SESSION_REVOKED = 'SESSION_REVOKED',
  USER_LOGGED_OUT = 'USER_LOGGED_OUT',
  LOGGED_OUT_ALL = 'LOGGED_OUT_ALL',
  PASSWORD_CHANGED = 'PASSWORD_CHANGED',
  IDENTIFY_TIMEOUT = 'IDENTIFY_TIMEOUT',
  SERVER_SHUTDOWN = 'SERVER_SHUTDOWN',
}

/**
 * Disconnect reasons that the server uses to forcibly kick a socket.
 * When we see these we must clear local auth state and route to login.
 */
export const KICK_DISCONNECT_REASONS: readonly DisconnectReason[] = [
  DisconnectReason.SESSION_REVOKED,
  DisconnectReason.USER_LOGGED_OUT,
  DisconnectReason.LOGGED_OUT_ALL,
  DisconnectReason.PASSWORD_CHANGED,
] as const;

/**
 * Generic envelope shape for every WS event. Mirrors the contract
 * documented in `WEBSOCKET-EVENTS.md`:
 *
 *   { type, payload, title, message, occurredAt, correlationId? }
 *
 * Both `title` and `message` are server-pre-localized (Persian for
 * fa-IR sockets, English for en sockets) — the client renders them
 * verbatim and does not translate. They are marked optional in TypeScript
 * only as a defensive hedge against legacy publishers that haven't been
 * updated yet; per spec they are required on every Kafka-sourced event.
 *
 * `correlationId` is present only on Kafka-sourced events and is used as
 * the de-dup key for the bell inbox.
 */
export interface WsEnvelope<TPayload = unknown> {
  type: WsEventType;
  payload: TPayload;
  /** Localized notification title shown above the message body. */
  title?: string;
  /** Localized human-readable body text. */
  message?: string;
  /** ISO timestamp the event was emitted. */
  occurredAt?: string;
  /** Present on Kafka-sourced events only; used to de-dup bell rows. */
  correlationId?: string;
  /**
   * Persisted in-app notification UUID from notification-management.
   * When present, the bell uses this for `mark-read` instead of a
   * client-synthesized id.
   */
  notificationId?: string;
  /**
   * A state refresh, not a notification (notification-management marks a
   * network cost's finality this way): update the views, add no bell entry,
   * show no toast.
   */
  silent?: boolean;
}

export interface IdentifyMessage {
  accessToken: string;
  deviceType: DeviceType;
  language?: SupportedLanguage;
  /** The `x-client-*` labels, shown in the owner's online list. */
  deviceName?: string;
  os?: string;
  browser?: string;
}

export interface IdentifiedPayload {
  socketId: string;
  userId: string;
  accountId: string;
  sessionId: string;
  role: string;
  language: SupportedLanguage;
}

export interface IdentifyFailedPayload {
  reason: IdentifyFailureReason;
}

export interface ConnectedDevice {
  socketId: string;
  userId: string;
  sessionId: string;
  /**
   * Display name parts of the user behind this connected device. Optional
   * because notification-management may not have the user record cached
   * yet at broadcast time. UI consumers should fall back to a generic
   * label (e.g. "Unknown user") when missing.
   */
  firstname?: string;
  lastname?: string;
  role: string;
  deviceType: DeviceType;
  language: SupportedLanguage;
  connectedAt: string;
  /**
   * Where the socket came from (null when unknown). An OWNER receives every
   * socket in the account; anyone else only their own user's.
   */
  ipAddress?: string | null;
  userAgent?: string | null;
  deviceName?: string | null;
  os?: string | null;
  browser?: string | null;
}

export interface LoginRecordedPayload {
  userId: string;
  sessionId: string;
  timestamp: string;
}

export interface DeviceListPayload {
  devices: ConnectedDevice[];
}

export interface DeviceDisconnectedPayload {
  socketId: string;
  userId: string;
  /** Lets a session-level list drop the session once its last socket is gone. */
  sessionId?: string;
  deviceType: DeviceType;
  reason: DisconnectReason;
}

// User events
export interface UserOnboardedWsPayload {
  userId: string;
  accountId: string;
  previousStatus: string;
  newStatus: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  createdAt: string;
  updatedAt: string;
  timestamp: string;
}

export interface UserRoleChangedWsPayload {
  userId: string;
  accountId: string;
  oldRole: string;
  newRole: string;
  timestamp: string;
}

export interface UserDeletedWsPayload {
  userId: string;
  accountId: string;
  timestamp: string;
  /**
   * Status the user held *before* deletion. Optional because older
   * notification-management deployments may omit it. Consumers patching the
   * dashboard `frozenUsersCount` widget should only decrement when this is
   * `'FROZEN'`. See the widget freshness map in WEBSOCKET-EVENTS.md.
   */
  previousStatus?: string;
}

export interface UserAccountStatusWsPayload {
  userId: string;
  accountId: string;
  reason?: string;
  timestamp: string;
}

// Account events
export interface AccountCreatedWsPayload {
  accountId: string;
  name: string;
  status: string;
  timestamp: string;
}

export interface AccountStatusWsPayload {
  accountId: string;
  reason?: string;
  timestamp: string;
  /**
   * Status the account held immediately before this transition.
   * Additive (newer notification-management releases). When present, lets
   * consumers update the `accountStatus` widget without an extra fetch.
   */
  previousStatus?: string;
  /**
   * Status the account holds after this transition. Additive — same
   * provenance as `previousStatus`.
   */
  newStatus?: string;
}

// Auth events
export interface AuthLoginSuccessWsPayload {
  userId: string;
  accountId: string;
  sessionId: string;
  timestamp: string;
}

export interface SessionCreatedWsPayload {
  sessionId: string;
  userId: string;
  accountId: string;
  timestamp: string;
}

export interface SessionRevokedWsPayload {
  sessionId: string;
  userId: string;
  accountId: string;
  reason?: string;
  timestamp: string;
}

export interface UserLoggedOutWsPayload {
  sessionId: string;
  userId: string;
  accountId: string;
  reason?: string;
  timestamp: string;
}

export interface UserLoggedOutAllWsPayload {
  userId: string;
  accountId: string;
  sessionCount: number;
  timestamp: string;
}

export interface UserPasswordChangedWsPayload {
  userId: string;
  accountId: string;
  timestamp: string;
}

export interface UserPasswordResetWsPayload {
  userId: string;
  accountId: string;
  reason?: string;
  timestamp: string;
}

export interface PasswordForgetTriggeredWsPayload {
  userId: string;
  accountId: string;
  timestamp: string;
}

export interface TokenReuseDetectedWsPayload {
  sessionId: string;
  userId: string;
  accountId: string;
  timestamp: string;
}

export interface GoogleAuthSetupCompletedWsPayload {
  userId: string;
  accountId: string;
  timestamp: string;
}

export interface GoogleAuthDisabledWsPayload {
  userId: string;
  accountId: string;
  timestamp: string;
}

export interface EmailChangedWsPayload {
  userId: string;
  accountId: string;
  email: string;
  timestamp: string;
}

// Vault & Wallet events
export interface VaultStatusWsPayload {
  vaultId: string;
  accountId: string;
  reason?: string;
  timestamp: string;
}

export interface VaultTotalBalanceUpdatedWsPayload {
  vaultId: string;
  accountId: string;
  source: string;
  currentTotalBalance: number | string;
  previousTotalBalance: number | string;
  delta: number | string;
  /**
   * USDT-denominated available balance after / before this update.
   * Additive — present only on newer publisher releases. When absent, fall
   * back to a fetch for the vault overview.
   */
  currentAvailableBalanceUsdt?: number | string;
  previousAvailableBalanceUsdt?: number | string;
  txId?: string;
  walletId?: string;
  finalizedAt?: string;
  pricedAt?: string;
}

export interface WalletCreatedWsPayload {
  walletId: string;
  accountId: string;
  vaultId: string;
  vaultName: string;
  assetNetworkId: string;
  name: string;
  status: string;
  address: string;
  balance: number | string;
  createdAt: string;
}

export interface WalletUpdatedWsPayload {
  walletId: string;
  accountId: string;
  name?: string;
  address?: string;
  updatedAt: string;
  /**
   * Additive: the publisher omits these unless they actually changed in
   * this update. When present, consumers can patch the wallet-settings
   * slice without re-fetching `/wallet/:id`.
   */
  withdrawalLimits?: WalletWithdrawalLimitSettingValue;
  timeBasedLimits?: WalletTimeLimitSettingValue;
}

export interface WalletStatusChangedWsPayload {
  walletId: string;
  accountId: string;
  /** Human-readable wallet name, added by the spec for in-message display. */
  name?: string;
  status: string;
  changedAt: string;
}

export interface WalletBalanceUpdatedWsPayload {
  walletId: string;
  vaultId: string;
  accountId: string;
  txId: string;
  direction: 'IN' | 'OUT' | string;
  delta: number | string;
  currentBalance: number | string;
  finalizedAt: string;
}

export interface WhitelistAddressCreatedWsPayload {
  whitelistAddressId: string;
  accountId: string;
  address: string;
  displayName: string;
  timestamp: string;
}

export interface WhitelistAddressStatusWsPayload {
  whitelistAddressId: string;
  accountId: string;
  /**
   * Human-readable label of the whitelist entry. Added by the
   * notification-management spec for in-message display; older publishers
   * may still omit it.
   */
  displayName?: string;
  timestamp: string;
}

export interface WithdrawalOperationAddedWsPayload {
  withdrawalOperationId: string;
  walletId: string;
  accountId: string;
  destinationAddress: string;
  destinationDisplayName?: string;
  amount: number | string;
  maxAcceptedFee: number | string;
  status: string;
  requestedAt: string;
}

export interface WithdrawalOperationUpdatedWsPayload {
  withdrawalOperationId: string;
  walletId: string;
  accountId: string;
  destinationAddress: string;
  destinationDisplayName?: string;
  amount: number | string;
  status: string;
  /**
   * Status the operation held immediately before this update. The spec
   * marks this as required for single-event count deltas on the dashboard
   * `inProgressWithdrawalsCount` widget; older publishers may still omit
   * it, in which case the consumer must re-fetch.
   */
  previousStatus?: string;
  reason?: string;
  updatedAt: string;
}

/**
 * `SIGNING_PROGRESS_UPDATED` — emitted as the MPC signing state machine
 * advances through milestones. `milestone` is one of `SUBMITTED`,
 * `POLLING_STARTED`, `COMPLETED`, `FAILED` per the notification-management
 * spec. `failureReason` is only meaningful when `milestone === 'FAILED'`.
 *
 * `occurredAt` lives in the payload as well as the envelope so the
 * step-3 timeline can pin each row to the originating milestone time
 * even when the envelope-root timestamp diverges (e.g. on re-broadcast).
 */
export interface SigningProgressUpdatedWsPayload {
  transactionId: string;
  walletId: string;
  accountId: string;
  milestone: 'SUBMITTED' | 'POLLING_STARTED' | 'COMPLETED' | 'FAILED' | string;
  failureReason?: string | null;
  occurredAt: string;
}

/**
 * `BLOCKCHAIN_CONFIRMATIONS_UPDATED` — emitted on every on-chain
 * confirmation increment for a broadcast transaction. Drives step 4's
 * blockchain-details refresh without re-polling
 * `/transaction/:id/blockchain-details`.
 *
 * Fired frequently (every block while the tx is being confirmed) — kept
 * out of the bell to avoid inbox flood; only the data slice consumes it.
 */
export interface BlockchainConfirmationsUpdatedWsPayload {
  transactionId: string;
  walletId: string;
  accountId: string;
  txid: string;
  confirmations: number;
  previousConfirmations: number;
  blockHeight: number;
  occurredAt: string;
}

/**
 * `TRANSACTION_BROADCASTED` — emitted the moment a signed tx is broadcast and a
 * chain `txid` is obtained. Drives step 4's network-details slice
 * (`transactionHash` + `explorerUrl`) and flips the parent status to
 * BROADCASTING / CONFIRMING without re-fetching `/transaction/:id/network-details`.
 */
export interface TransactionBroadcastedWsPayload {
  transactionId: string;
  walletId: string;
  accountId: string;
  txid: string;
  /** Pre-built block-explorer URL for `txid`; null when the protocol is unknown. */
  explorerUrl: string | null;
  /** Resulting projection status (BROADCASTING / CONFIRMING). */
  status: string;
  occurredAt: string;
}

// Transaction events
export interface TransactionFinalizedWsPayload {
  walletId: string;
  accountId: string;
  txid: string;
  direction: 'IN' | 'OUT' | string;
  /** TRANSFER for deposits and withdrawals; otherwise a network cost the platform paid from the wallet. */
  kind?: string;
  amount: number | string;
  fee: number | string;
  amountBtc?: number | string;
  amountUsdt?: number | string;
  finalizedAt: string;
}

export interface TransactionFailedWsPayload {
  withdrawalOperationId: string;
  walletId: string;
  accountId: string;
  failureReason: string;
  failurePhase: string;
  failedAt: string;
}

// Policy events
export interface PolicyEventWsPayload {
  policyId: string;
  accountId: string;
  domain: string;
  resource: string;
  action: string;
  scopeType: string;
  status: string;
  version?: string;
  previousVersion?: string;
  newVersion?: string;
  /** Set on create / update / activate / deactivate events. */
  changeReason?: string;
  /** Set on delete events only — mutually exclusive with `changeReason`. */
  deleteReason?: string;
  timestamp: string;
}

export interface PolicyRequestedWsPayload {
  requestId: string;
  policyId?: string;
  accountId: string;
  policyDomain: string;
  policyResource: string;
  policyAction: string;
  currentStageNo: number;
  totalStages: number;
  expiresAt: string;
  ttlMinutes: number;
  timestamp: string;
}

// Approval events
export interface ApprovalRequestedWsPayload {
  requestId: string;
  accountId: string;
  domain: string;
  resourceType: string;
  action: string;
  scopeType: string;
  requesterRole: string;
  timestamp: string;
  /**
   * Set by policy-management once approver sets exist for the request.
   * When present, consumers can apply per-user `pendingApprovalsCount`
   * deltas without re-fetching the dashboard overview.
   */
  approverUserIds?: string[];
}

export interface ApprovalDecisionWsPayload {
  requestId: string;
  accountId: string;
  domain: string;
  resourceType: string;
  action: string;
  approvalType: string;
  autoApproved: boolean;
  reason?: string;
  stageNo: number;
  totalStages: number;
  timestamp: string;
}

export interface ApprovalStageCompletedWsPayload {
  requestId: string;
  accountId: string;
  domain: string;
  resourceType: string;
  action: string;
  completedStageNo: number;
  nextStageNo?: number;
  totalStages: number;
  timestamp: string;
  /**
   * Users eligible for `nextStageNo`. Additive — when present, consumers
   * can patch each user's `pendingApprovalsCount` without re-fetching.
   */
  approverUserIds?: string[];
}

export interface ApprovalRequestExpiredWsPayload {
  requestId: string;
  accountId: string;
  domain: string;
  resourceType: string;
  action: string;
  stageNo: number;
  expiredAt: string;
}

export interface KillSwitchExecutedWsPayload {
  killSwitchOperationId: string;
  accountId: string;
  reason?: string;
  executedAt: string;
  timestamp: string;
}

/**
 * `KILL_SWITCH_RELEASED` — emitted by user-management when panic mode is
 * cleared on unfreeze. Per the dashboard widget freshness map, this drives
 * the `accountStatus` widget back to its non-frozen state, in tandem with
 * `ACCOUNT_UNFROZEN`. `killSwitchOperationId` is nullable because the
 * release isn't always tied to a specific prior execution (e.g. an admin
 * unfreeze that bypassed a kill-switch op).
 */
export interface KillSwitchReleasedWsPayload {
  accountId: string;
  killSwitchOperationId: string | null;
  releasedBy: string;
  reason?: string;
  releasedAt: string;
  timestamp: string;
}

/**
 * `ACCOUNT_TOTAL_BALANCE_UPDATED` — account-level aggregate of
 * `VAULT_TOTAL_BALANCE_UPDATED`. Per the dashboard widget freshness map
 * this is the *preferred* source for the `totalBalance` /
 * `availableBalanceUsdt` widgets because it matches the gateway's
 * aggregate response shape directly.
 */
export interface AccountTotalBalanceUpdatedWsPayload {
  accountId: string;
  currentTotalBalanceUsdt: number | string;
  previousTotalBalanceUsdt: number | string;
  currentAvailableBalanceUsdt: number | string;
  previousAvailableBalanceUsdt: number | string;
  pricedAt: string;
}
